import { useState, useCallback } from 'react'
import { ThreeEvent } from '@react-three/fiber'

/**
 * useDragDetection Hook
 *
 * Detects if the user is dragging (moving the mouse significantly) vs clicking.
 * Used to prevent brick placement during camera orbit (Task 1.4.7).
 *
 * Strategy:
 * 1. Track mouse position on pointer down
 * 2. Track distance moved during pointer move
 * 3. On click, calculate total distance moved
 * 4. If distance > threshold (5px), consider it a drag and return true
 *
 * Usage:
 * ```tsx
 * const { handlePointerDown, handlePointerMove, isDrag } = useDragDetection()
 *
 * <mesh
 *   onPointerDown={handlePointerDown}
 *   onPointerMove={handlePointerMove}
 *   onClick={(e) => {
 *     if (isDrag(e)) return // Don't handle click if dragging
 *     // Handle click...
 *   }}
 * >
 * ```
 *
 * @param threshold - Distance in pixels to consider a drag (default: 5)
 * @returns Handlers and drag detection function
 */
export const useDragDetection = (threshold = 5) => {
  const [mouseDownPosition, setMouseDownPosition] = useState<{ x: number; y: number } | null>(null)
  const [isDragging, setIsDragging] = useState(false)

  /**
   * Track initial mouse position on pointer down
   */
  const handlePointerDown = useCallback((event: ThreeEvent<PointerEvent>) => {
    setMouseDownPosition({ x: event.clientX, y: event.clientY })
    setIsDragging(false)
  }, [])

  /**
   * Track mouse movement and detect if dragging
   */
  const handlePointerMove = useCallback((event: ThreeEvent<PointerEvent>) => {
    if (!mouseDownPosition) return

    const dx = event.clientX - mouseDownPosition.x
    const dy = event.clientY - mouseDownPosition.y
    const distance = Math.sqrt(dx * dx + dy * dy)

    // If moved more than threshold, consider it a drag
    if (distance > threshold) {
      setIsDragging(true)
    }
  }, [mouseDownPosition, threshold])

  /**
   * Check if the click event was actually a drag
   * Calculates distance between initial mousedown and current click position
   *
   * @param event - Click event from R3F
   * @returns true if this was a drag, false if it was a stationary click
   */
  const isDrag = useCallback((event: ThreeEvent<MouseEvent>): boolean => {
    // If no mouse down position tracked, assume not a drag
    if (!mouseDownPosition) {
      return false
    }

    const dx = event.clientX - mouseDownPosition.x
    const dy = event.clientY - mouseDownPosition.y
    const distance = Math.sqrt(dx * dx + dy * dy)

    // Reset state after check
    setMouseDownPosition(null)
    setIsDragging(false)

    // Return true if distance exceeds threshold or we already detected dragging
    return distance > threshold || isDragging
  }, [mouseDownPosition, isDragging, threshold])

  /**
   * Reset drag detection state
   * Useful for cleanup or manual reset
   */
  const reset = useCallback(() => {
    setMouseDownPosition(null)
    setIsDragging(false)
  }, [])

  return {
    handlePointerDown,
    handlePointerMove,
    isDrag,
    reset,
  }
}
